import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import GMRInput from '../components/GMRInput';
import DocumentUpload from '../components/DocumentUpload';
import MaterialsUsed from '../components/MaterialsUsed';
import VitalCard from '../components/VitalCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Edit, Trash2, Calendar, FileText, Plus, Clock, Printer, CheckCircle, Lock, AlertTriangle, Heart, Pill, AlertCircle, Thermometer, DollarSign, Receipt, CreditCard, FileUp, Package } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EncounterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const printRef = useRef();
  const [encounter, setEncounter] = useState(null);
  const [patient, setPatient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showSignConfirm, setShowSignConfirm] = useState(false);
  const [formData, setFormData] = useState({});
  const [noteData, setNoteData] = useState({ note_text: '', is_amendment: false });
  const [saving, setSaving] = useState(false);
  
  // Billing Panel State
  const [invoice, setInvoice] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [addingService, setAddingService] = useState(false);
  const [consultationService, setConsultationService] = useState(null);
  
  // Invoice Ready Modal State
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [modalServiceId, setModalServiceId] = useState('');
  const [addingModalService, setAddingModalService] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [encounterRes, notesRes, servicesRes] = await Promise.all([
        axios.get(`${API}/encounters/${id}`),
        axios.get(`${API}/notes?encounter_id=${id}`),
        axios.get(`${API}/services?active_only=true&billable_only=true`)
      ]);
      setEncounter(encounterRes.data);
      setFormData(encounterRes.data);
      setNotes(notesRes.data);
      setServices(servicesRes.data);
      
      // Find consultation service for preload display
      const consultation = servicesRes.data.find(s => s.category === 'consultation');
      setConsultationService(consultation);
      
      const patientRes = await axios.get(`${API}/patients/${encounterRes.data.patient_id}`);
      setPatient(patientRes.data);
      
      // Fetch invoice if exists
      try {
        const invoiceRes = await axios.get(`${API}/encounters/${id}/invoice`);
        setInvoice(invoiceRes.data);
      } catch (err) {
        setInvoice(null);
      }
    } catch (error) {
      console.error('Failed to fetch encounter:', error);
      toast.error('Failed to load encounter');
      navigate('/encounters');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${API}/encounters/${id}`, {
        date: formData.date,
        presenting_complaint: formData.presenting_complaint,
        history_of_presenting_complaint: formData.history_of_presenting_complaint,
        examination_findings: formData.examination_findings,
        assessment: formData.assessment,
        plan: formData.plan,
        temperature: formData.temperature,
        bp_systolic: formData.bp_systolic,
        bp_diastolic: formData.bp_diastolic,
        pulse_rate: formData.pulse_rate,
        oxygen_saturation: formData.oxygen_saturation,
        gmr: formData.gmr,
        gmr_unit: formData.gmr_unit,
        dip_stick: formData.dip_stick,
        height: formData.height,
        weight: formData.weight
      });
      setEncounter(response.data);
      setShowEditModal(false);
      toast.success('Encounter updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update encounter');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/encounters/${id}`);
      toast.success('Encounter deleted');
      navigate('/encounters');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete encounter');
    }
  };

  const handleSign = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/encounters/${id}/sign`);
      setEncounter(response.data);
      setShowSignConfirm(false);
      toast.success('Encounter signed successfully');
      
      // Check if invoice summary is in response and show modal
      if (response.data.invoice_summary) {
        setInvoiceSummary(response.data.invoice_summary);
        setShowInvoiceModal(true);
        // Also update the invoice state for billing panel
        setInvoice(response.data.invoice_summary);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign encounter');
    } finally {
      setSaving(false);
    }
  };
  
  // Add service to encounter (creates invoice if not exists)
  const handleAddService = async () => {
    if (!selectedServiceId) {
      toast.error('Please select a service');
      return;
    }
    
    setAddingService(true);
    try {
      const response = await axios.post(`${API}/encounters/${id}/add-service?service_id=${selectedServiceId}&quantity=1`);
      toast.success(response.data.message);
      setSelectedServiceId('');
      
      // Refresh invoice data
      const invoiceRes = await axios.get(`${API}/encounters/${id}/invoice`);
      setInvoice(invoiceRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add service');
    } finally {
      setAddingService(false);
    }
  };
  
  // Add service from Invoice Modal (doctor adding services before finalization)
  const handleAddServiceFromModal = async () => {
    if (!modalServiceId || !invoiceSummary?.invoice_id) {
      toast.error('Please select a service');
      return;
    }
    
    setAddingModalService(true);
    try {
      // Add service to the encounter's invoice
      const response = await axios.post(`${API}/encounters/${id}/add-service?service_id=${modalServiceId}&quantity=1`);
      toast.success('Service added to invoice');
      setModalServiceId('');
      
      // Refresh invoice summary for modal
      const invoiceRes = await axios.get(`${API}/encounters/${id}/invoice`);
      if (invoiceRes.data) {
        setInvoiceSummary({
          invoice_id: invoiceRes.data.id,
          invoice_number: invoiceRes.data.invoice_number,
          patient_name: invoiceRes.data.patient_name,
          line_items: invoiceRes.data.line_items || [],
          subtotal: invoiceRes.data.subtotal || 0,
          grand_total: invoiceRes.data.grand_total || 0,
          patient_total: invoiceRes.data.patient_total || invoiceRes.data.patient_portion || 0,
          insurer_total: invoiceRes.data.insurer_total || invoiceRes.data.insurer_portion || 0,
          status: invoiceRes.data.status,
          is_insurance_invoice: invoiceRes.data.is_insurance_invoice,
          insurance_provider_name: invoiceRes.data.insurance_provider_name
        });
        setInvoice(invoiceRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add service');
    } finally {
      setAddingModalService(false);
    }
  };
  
  // Finalize invoice from modal
  const handleFinalizeInvoice = async () => {
    if (!invoiceSummary?.invoice_id) return;
    
    setFinalizing(true);
    try {
      await axios.post(`${API}/invoices/${invoiceSummary.invoice_id}/finalize`);
      toast.success('Invoice finalized - sent to Cashier');
      setShowInvoiceModal(false);
      
      // Refresh invoice
      const invoiceRes = await axios.get(`${API}/encounters/${id}/invoice`);
      setInvoice(invoiceRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize invoice');
    } finally {
      setFinalizing(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteData.note_text.trim()) {
      toast.error('Please enter a note');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/notes`, {
        encounter_id: id,
        author_name: user.full_name,
        role: user.role,
        note_text: noteData.note_text,
        is_amendment: encounter?.signed_at ? true : false
      });
      toast.success(encounter?.signed_at ? 'Amendment added' : 'Note added');
      setShowNoteModal(false);
      setNoteData({ note_text: '', is_amendment: false });
      const notesRes = await axios.get(`${API}/notes?encounter_id=${id}`);
      setNotes(notesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const isSigned = encounter?.signed_at != null;
  const canEdit = !isSigned;
  const canSign = user?.role === 'owner' && !isSigned;

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[50vh]">
          <div className="text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!encounter || !patient) return null;

  return (
    <Layout>
      <div className="page-container" data-testid="encounter-detail-page">
        {/* Header */}
        <div className="mb-6 no-print">
          <button
            onClick={() => navigate('/encounters')}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Encounters
          </button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="page-title">{patient.full_name} {patient.middle_name}</h1>
                {isSigned && (
                  <span className="flex items-center gap-1 text-sm bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">
                    <CheckCircle className="w-4 h-4" />
                    Signed
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-2">
                <span className="flex items-center gap-2 text-slate-500">
                  <Calendar className="w-4 h-4" />
                  {encounter.date}
                </span>
                <span className="text-slate-500">Age: {calculateAge(patient.date_of_birth)} years</span>
                {isSigned && (
                  <span className="text-sm text-slate-500">
                    Signed by {encounter.signed_by_name} on {formatDateTime(encounter.signed_at)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handlePrint}
                className="flex items-center gap-2"
                data-testid="print-encounter-btn"
              >
                <Printer className="w-4 h-4" />
                Print
              </Button>
              {canSign && (
                <Button
                  onClick={() => setShowSignConfirm(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                  data-testid="sign-encounter-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  Sign Encounter
                </Button>
              )}
              {canEdit && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setShowEditModal(true)}
                    className="flex items-center gap-2"
                    data-testid="edit-encounter-btn"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-encounter-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Printable Content */}
        <div ref={printRef} className="print-content">
          {/* Print Header */}
          <div className="print-only print-header">
            <h1 className="text-2xl font-bold">My Med Pal</h1>
            <p className="text-sm text-gray-600">Clinical Encounter Record</p>
            <hr className="my-4 border-gray-300" />
          </div>

          {/* Patient Demographics for Print */}
          <div className="print-only print-patient-info">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500">Patient Name</p>
                <p className="font-semibold">{patient.full_name} {patient.middle_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Date of Birth</p>
                <p className="font-semibold">{patient.date_of_birth}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Age</p>
                <p className="font-semibold">{calculateAge(patient.date_of_birth)} years</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Gender</p>
                <p className="font-semibold capitalize">{patient.gender}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Parish</p>
                <p className="font-semibold">{patient.parish || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Encounter Date</p>
                <p className="font-semibold">{encounter.date}</p>
              </div>
            </div>
            
            {/* Clinical Summary in Print */}
            <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
              <div className="border p-2">
                <p className="font-semibold text-red-700">Allergies</p>
                <p>{patient.allergies || 'None documented'}</p>
              </div>
              <div className="border p-2">
                <p className="font-semibold text-amber-700">Chronic Illnesses</p>
                <p>{patient.chronic_illnesses || 'None documented'}</p>
              </div>
              <div className="border p-2">
                <p className="font-semibold text-blue-700">Current Medications</p>
                <p>{patient.current_medications || 'None documented'}</p>
              </div>
            </div>
            <hr className="my-4 border-gray-300" />
          </div>

          {/* Clinical Summary (Screen) */}
          <div className="card mb-6 no-print" data-testid="clinical-summary">
            <div className="p-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900 font-heading flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Patient Clinical Summary
              </h2>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 border rounded-lg p-3">
                <p className="text-xs text-slate-500 font-semibold mb-1">Age</p>
                <p className="text-lg font-bold text-slate-900">{calculateAge(patient.date_of_birth)} years</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-1 mb-1">
                  <AlertCircle className="w-3 h-3 text-red-600" />
                  <p className="text-xs font-semibold text-red-700">Allergies</p>
                </div>
                <p className="text-sm text-red-900">{patient.allergies || 'None'}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-1 mb-1">
                  <Heart className="w-3 h-3 text-amber-600" />
                  <p className="text-xs font-semibold text-amber-700">Chronic Illnesses</p>
                </div>
                <p className="text-sm text-amber-900">{patient.chronic_illnesses || 'None'}</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-1 mb-1">
                  <Pill className="w-3 h-3 text-blue-600" />
                  <p className="text-xs font-semibold text-blue-700">Current Medications</p>
                </div>
                <p className="text-sm text-blue-900">{patient.current_medications || 'None'}</p>
              </div>
            </div>
          </div>

          {/* Vitals */}
          <div className="card mb-6 print-section" data-testid="vitals-section">
            <div className="p-4 border-b border-slate-200">
              <h2 className="font-semibold text-slate-900 font-heading flex items-center gap-2">
                <Thermometer className="w-5 h-5 text-blue-500 no-print" />
                Vitals & Examination
                {isSigned && <Lock className="w-4 h-4 text-slate-400 no-print" />}
              </h2>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-4 md:grid-cols-8 gap-4 text-center">
                <VitalCard 
                  label="Temp" 
                  value={encounter.temperature} 
                  unit="°C" 
                  thresholdKey="temperature" 
                />
                <VitalCard 
                  label="BP" 
                  isBP={true}
                  systolic={encounter.bp_systolic}
                  diastolic={encounter.bp_diastolic}
                  unit="mmHg" 
                />
                <VitalCard 
                  label="Pulse" 
                  value={encounter.pulse_rate} 
                  unit="bpm" 
                  thresholdKey="pulse_rate" 
                />
                <VitalCard 
                  label="SpO₂" 
                  value={encounter.oxygen_saturation} 
                  unit="%" 
                  thresholdKey="oxygen_saturation" 
                />
                <VitalCard 
                  label="GMR" 
                  value={encounter.gmr} 
                  unit={encounter.gmr_unit || 'mg/dL'} 
                  thresholdKey="gmr"
                  gmrUnit={encounter.gmr_unit || 'mg/dL'}
                />
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-600 font-medium">Dip Stick</p>
                  <p className="font-bold text-slate-900 truncate">{encounter.dip_stick || '-'}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-600 font-medium">Height</p>
                  <p className="font-bold text-slate-900">{encounter.height || '-'}</p>
                  <p className="text-xs text-slate-500">cm</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-600 font-medium">Weight</p>
                  <p className="font-bold text-slate-900">{encounter.weight || '-'}</p>
                  <p className="text-xs text-slate-500">kg</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Clinical Narrative */}
            <div className="lg:col-span-2 space-y-4">
              {/* Presenting Complaint */}
              <div className="card p-4 print-section border border-slate-200" data-testid="pc-section">
                <h3 className="font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
                  Presenting Complaint
                  {isSigned && <Lock className="w-4 h-4 text-slate-400 no-print" />}
                </h3>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[60px] print-text">
                  {encounter.presenting_complaint || 'Not documented'}
                </div>
              </div>

              {/* History of Presenting Complaint */}
              <div className="card p-4 print-section border border-slate-200" data-testid="hpc-section">
                <h3 className="font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
                  History of Presenting Complaint
                  {isSigned && <Lock className="w-4 h-4 text-slate-400 no-print" />}
                </h3>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[60px] print-text">
                  {encounter.history_of_presenting_complaint || 'Not documented'}
                </div>
              </div>

              {/* Examination Findings */}
              <div className="card p-4 print-section border border-slate-200" data-testid="exam-section">
                <h3 className="font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
                  Examination Findings
                  {isSigned && <Lock className="w-4 h-4 text-slate-400 no-print" />}
                </h3>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[60px] print-text">
                  {encounter.examination_findings || 'Not documented'}
                </div>
              </div>

              {/* Assessment */}
              <div className="card p-4 print-section border border-slate-200" data-testid="assessment-section">
                <h3 className="font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
                  Assessment
                  {isSigned && <Lock className="w-4 h-4 text-slate-400 no-print" />}
                </h3>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[60px] print-text">
                  {encounter.assessment || 'Not documented'}
                </div>
              </div>

              {/* Plan */}
              <div className="card p-4 print-section border border-slate-200" data-testid="plan-section">
                <h3 className="font-semibold text-slate-800 mb-3 pb-2 border-b border-slate-200 flex items-center gap-2">
                  Plan
                  {isSigned && <Lock className="w-4 h-4 text-slate-400 no-print" />}
                </h3>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-800 whitespace-pre-wrap min-h-[60px] print-text">
                  {encounter.plan || 'Not documented'}
                </div>
              </div>
            </div>

            {/* Doctor's Notes */}
            <div className="card print-section border border-slate-200" data-testid="doctors-notes">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-semibold text-slate-800 font-heading">Doctor&apos;s Notes</h2>
                <Button
                  size="sm"
                  onClick={() => setShowNoteModal(true)}
                  className="btn-primary flex items-center gap-1 no-print"
                  data-testid="add-note-btn"
                >
                  <Plus className="w-4 h-4" />
                  {isSigned ? 'Amendment' : 'Add'}
                </Button>
              </div>
              <div className="p-4 max-h-[500px] overflow-y-auto print-notes">
                {notes.length > 0 ? (
                  <div className="space-y-3">
                    {notes.map((note) => (
                      <div 
                        key={note.id} 
                        className={`note-card border border-slate-200 ${note.is_amendment ? 'border-amber-300 bg-amber-50' : ''}`} 
                        data-testid={`note-${note.id}`}
                      >
                        {note.is_amendment && (
                          <div className="flex items-center gap-1 text-amber-700 text-xs font-semibold mb-2">
                            <AlertTriangle className="w-3 h-3" />
                            AMENDMENT
                          </div>
                        )}
                        <div className="note-header">
                          <div className="flex items-center">
                            <span className="note-author">{note.author_name}</span>
                            <span className="note-role capitalize">({note.role})</span>
                          </div>
                          <span className="note-time flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(note.created_at)}
                          </span>
                        </div>
                        <p className="note-text">{note.note_text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500 no-print">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm">No notes yet</p>
                  </div>
                )}
              </div>
              <div className="p-3 border-t border-slate-200 bg-amber-50 no-print">
                <p className="text-xs text-amber-700">
                  Doctor&apos;s Notes are append-only.
                  {isSigned && ' Only amendments can be added.'}
                </p>
              </div>
            </div>

            {/* BILLING PANEL - Visible During Assessment */}
            <div className="card print-section border border-slate-200 no-print" data-testid="billing-panel">
              <div className="p-4 border-b border-slate-200 bg-emerald-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    <h2 className="font-semibold text-slate-800 font-heading">Billing</h2>
                  </div>
                  {invoice && (
                    <span className={`text-xs px-2 py-1 rounded ${
                      invoice.status === 'draft' ? 'bg-amber-100 text-amber-700' :
                      invoice.status === 'finalized' ? 'bg-blue-100 text-blue-700' :
                      invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {invoice.invoice_number} - {invoice.status?.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="p-4">
                {/* Invoice Status */}
                {invoice ? (
                  <div className="space-y-4">
                    {/* Insurance Info */}
                    {invoice.is_insurance_invoice && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                        <div className="flex items-center gap-2 text-blue-700 font-medium mb-1">
                          <CreditCard className="w-4 h-4" />
                          Insurance: {invoice.insurance_provider_name}
                        </div>
                        <p className="text-blue-600 text-xs">Policy: {invoice.policy_number}</p>
                      </div>
                    )}
                    
                    {/* Line Items */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Line Items</h4>
                      {invoice.line_items?.length > 0 ? (
                        <div className="space-y-2">
                          {invoice.line_items.map((item, idx) => (
                            <div key={item.id || idx} className="flex justify-between items-center text-sm bg-slate-50 p-2 rounded">
                              <div>
                                <span className="font-medium">{item.description}</span>
                                {item.category && (
                                  <span className="text-xs text-slate-500 ml-2 capitalize">({item.category})</span>
                                )}
                              </div>
                              <span className="text-slate-700">${(item.final_total || item.total || 0).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500">No items yet</p>
                      )}
                    </div>
                    
                    {/* Consultation Preload Notice */}
                    {!isSigned && consultationService && !invoice.line_items?.some(i => i.category === 'consultation') && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Consultation (${consultationService.default_price?.toFixed(2)}) will be added on sign</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Totals */}
                    <div className="border-t pt-3">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span>${(invoice.subtotal || 0).toFixed(2)}</span>
                      </div>
                      {invoice.is_insurance_invoice && (
                        <>
                          <div className="flex justify-between text-sm text-blue-600">
                            <span>Insurer Portion:</span>
                            <span>${(invoice.insurer_total || invoice.insurer_portion || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-emerald-600">
                            <span>Patient Portion:</span>
                            <span>${(invoice.patient_total || invoice.patient_portion || 0).toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between font-semibold text-base mt-2 pt-2 border-t">
                        <span>Total:</span>
                        <span>${(invoice.grand_total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                    
                    {/* View Invoice Link */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                      className="w-full"
                    >
                      <Receipt className="w-4 h-4 mr-2" />
                      View Full Invoice
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* No Invoice Yet */}
                    <div className="text-center py-4 text-slate-500">
                      <Receipt className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                      <p className="text-sm">No invoice created yet</p>
                      {consultationService && !isSigned && (
                        <p className="text-xs text-amber-600 mt-2">
                          Invoice with consultation (${consultationService.default_price?.toFixed(2)}) will be created on sign
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Add Service (for doctor/nurse during assessment) */}
                {!isSigned && ['owner', 'doctor', 'nurse'].includes(user?.role) && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Add Service</h4>
                    <div className="flex gap-2">
                      <Select value={selectedServiceId} onValueChange={setSelectedServiceId}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select a service..." />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map(service => (
                            <SelectItem key={service.id} value={service.id}>
                              {service.name} - ${service.default_price?.toFixed(2)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={handleAddService}
                        disabled={!selectedServiceId || addingService}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {addingService ? 'Adding...' : 'Add'}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Adding a service will automatically create an invoice if one doesn&apos;t exist.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Materials Used (Packet B) */}
            <div className="card border border-slate-200 no-print">
              <div className="p-4 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-slate-600" />
                  <h2 className="font-semibold text-slate-800 font-heading">Materials Used</h2>
                </div>
              </div>
              <div className="p-4">
                <MaterialsUsed 
                  encounterId={encounter?.id}
                  userRole={user?.role}
                  isSigned={isSigned}
                />
              </div>
            </div>

            {/* Encounter Documents (Packet 3) */}
            <div className="card border border-slate-200 no-print">
              <div className="p-4 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2">
                  <FileUp className="w-5 h-5 text-slate-600" />
                  <h2 className="font-semibold text-slate-800 font-heading">Documents</h2>
                </div>
              </div>
              <div className="p-4">
                <DocumentUpload 
                  patientId={encounter?.patient_id} 
                  encounterId={encounter?.id} 
                />
              </div>
            </div>
          </div>

          {/* Print Signature Block */}
          <div className="print-only print-signature">
            <hr className="my-6 border-gray-300" />
            <div className="grid grid-cols-2 gap-8 mt-8">
              <div>
                <p className="text-sm text-gray-500 mb-1">Provider Signature</p>
                <div className="border-b border-gray-400 h-8"></div>
                <p className="mt-2 font-semibold">{encounter.signed_by_name || user?.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Date/Time Signed</p>
                <div className="border-b border-gray-400 h-8"></div>
                <p className="mt-2">{encounter.signed_at ? formatDateTime(encounter.signed_at) : '_________________'}</p>
              </div>
            </div>
            {isSigned && (
              <p className="text-center mt-6 text-sm text-gray-600">
                This document was electronically signed and is a legal medical record.
              </p>
            )}
          </div>
        </div>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-encounter-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Encounter</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-6">
              <div>
                <Label htmlFor="edit_date">Date</Label>
                <Input
                  id="edit_date"
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="edit-date-input"
                />
              </div>

              {/* Vitals */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Vitals</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label>Temp (°C)</Label>
                    <Input
                      value={formData.temperature || ''}
                      onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>BP (mmHg)</Label>
                    <div className="flex gap-1">
                      <Input
                        value={formData.bp_systolic || ''}
                        onChange={(e) => setFormData({ ...formData, bp_systolic: e.target.value })}
                        placeholder="Sys"
                        className="w-1/2"
                      />
                      <Input
                        value={formData.bp_diastolic || ''}
                        onChange={(e) => setFormData({ ...formData, bp_diastolic: e.target.value })}
                        placeholder="Dia"
                        className="w-1/2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Pulse (bpm)</Label>
                    <Input
                      value={formData.pulse_rate || ''}
                      onChange={(e) => setFormData({ ...formData, pulse_rate: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>SpO₂ (%)</Label>
                    <Input
                      value={formData.oxygen_saturation || ''}
                      onChange={(e) => setFormData({ ...formData, oxygen_saturation: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <GMRInput
                      value={formData.gmr || ''}
                      unit={formData.gmr_unit || 'mg/dL'}
                      onChange={(val) => setFormData({ ...formData, gmr: val })}
                      onUnitChange={(unit) => setFormData({ ...formData, gmr_unit: unit })}
                      label="Blood Glucose (GMR)"
                    />
                  </div>
                  <div>
                    <Label>Dip Stick</Label>
                    <Input
                      value={formData.dip_stick || ''}
                      onChange={(e) => setFormData({ ...formData, dip_stick: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Height (cm)</Label>
                    <Input
                      value={formData.height || ''}
                      onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Weight (kg)</Label>
                    <Input
                      value={formData.weight || ''}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Narrative */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Clinical Narrative</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Presenting Complaint</Label>
                    <Textarea
                      value={formData.presenting_complaint || ''}
                      onChange={(e) => setFormData({ ...formData, presenting_complaint: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>History of Presenting Complaint</Label>
                    <Textarea
                      value={formData.history_of_presenting_complaint || ''}
                      onChange={(e) => setFormData({ ...formData, history_of_presenting_complaint: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Examination Findings</Label>
                    <Textarea
                      value={formData.examination_findings || ''}
                      onChange={(e) => setFormData({ ...formData, examination_findings: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Assessment</Label>
                    <Textarea
                      value={formData.assessment || ''}
                      onChange={(e) => setFormData({ ...formData, assessment: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Plan</Label>
                    <Textarea
                      value={formData.plan || ''}
                      onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="update-encounter-btn">
                  {saving ? 'Saving...' : 'Update'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Note Modal */}
        <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
          <DialogContent className="sm:max-w-lg" data-testid="add-note-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {isSigned ? 'Add Amendment' : "Add Doctor's Note"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddNote} className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">
                  <strong>Author:</strong> {user?.full_name}
                </p>
                <p className="text-sm text-slate-600 capitalize">
                  <strong>Role:</strong> {user?.role}
                </p>
              </div>
              {isSigned && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    This encounter is signed. Your note will be recorded as an <strong>Amendment</strong>.
                  </p>
                </div>
              )}
              <div>
                <Label htmlFor="note_text">{isSigned ? 'Amendment Text *' : 'Note *'}</Label>
                <Textarea
                  id="note_text"
                  value={noteData.note_text}
                  onChange={(e) => setNoteData({ ...noteData, note_text: e.target.value })}
                  placeholder={isSigned ? "Enter your amendment..." : "Enter your note..."}
                  rows={6}
                  required
                  data-testid="note-text-input"
                />
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  <strong>Warning:</strong> Once saved, this cannot be edited or deleted.
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowNoteModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-note-btn">
                  {saving ? 'Saving...' : isSigned ? 'Save Amendment' : 'Save Note'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Sign Confirmation Modal */}
        <Dialog open={showSignConfirm} onOpenChange={setShowSignConfirm}>
          <DialogContent className="sm:max-w-md" data-testid="sign-confirm-modal">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                Sign Encounter
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-600">
                Are you sure you want to sign this encounter? Once signed:
              </p>
              <ul className="text-sm text-slate-600 space-y-2 ml-4">
                <li className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  Clinical narrative and vitals become read-only
                </li>
                <li className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  Encounter cannot be deleted
                </li>
                <li className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Only amendments can be added
                </li>
              </ul>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">
                  <strong>Signing as:</strong> {user?.full_name}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Date/Time:</strong> {new Date().toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowSignConfirm(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSign}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={saving}
                data-testid="confirm-sign-btn"
              >
                {saving ? 'Signing...' : 'Sign Encounter'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="sm:max-w-md" data-testid="delete-confirm-modal">
            <DialogHeader>
              <DialogTitle className="font-heading">Delete Encounter</DialogTitle>
            </DialogHeader>
            <p className="text-slate-600">
              Are you sure you want to delete this encounter? All associated notes will also be deleted.
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Invoice Ready Modal - Editable Draft Invoice */}
        <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="invoice-ready-modal">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2 text-emerald-700">
                <Receipt className="w-6 h-6" />
                Invoice - Draft
              </DialogTitle>
              <p className="text-sm text-slate-500 mt-1">
                Add services before finalizing. Finalized invoices go to Cashier.
              </p>
            </DialogHeader>
            
            {invoiceSummary && (
              <div className="space-y-4">
                {/* Invoice Header */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-lg font-semibold text-slate-800">{invoiceSummary.invoice_number}</p>
                      <p className="text-sm text-slate-600">{invoiceSummary.patient_name}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 font-medium">
                        DRAFT
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        invoiceSummary.is_insurance_invoice 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {invoiceSummary.is_insurance_invoice ? invoiceSummary.insurance_provider_name : 'Self-Pay'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* ADD SERVICE SECTION - Doctor can add more services */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Service to Invoice
                  </h4>
                  <div className="flex gap-2">
                    <Select value={modalServiceId} onValueChange={setModalServiceId}>
                      <SelectTrigger className="flex-1 bg-white">
                        <SelectValue placeholder="Select a service to add..." />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map(service => (
                          <SelectItem key={service.id} value={service.id}>
                            <div className="flex justify-between items-center w-full">
                              <span>{service.name}</span>
                              <span className="text-slate-500 ml-2">${service.default_price?.toFixed(2)}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleAddServiceFromModal}
                      disabled={!modalServiceId || addingModalService}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {addingModalService ? 'Adding...' : 'Add'}
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Select services from the catalogue. Prices are fixed from Service Catalogue.
                  </p>
                </div>
                
                {/* Line Items */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">
                    Services ({invoiceSummary.line_items?.length || 0} items)
                  </h4>
                  <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                    {invoiceSummary.line_items?.length > 0 ? (
                      invoiceSummary.line_items.map((item, idx) => (
                        <div key={item.id || idx} className="flex justify-between items-center p-3 text-sm">
                          <div>
                            <span className="font-medium">{item.description}</span>
                            {item.category && (
                              <span className="text-xs text-slate-400 ml-2 capitalize">({item.category})</span>
                            )}
                            {item.quantity > 1 && <span className="text-slate-500"> x{item.quantity}</span>}
                          </div>
                          <span className="font-medium">${(item.final_total || item.total || 0).toFixed(2)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-slate-500 text-sm">
                        No services added yet
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Totals */}
                <div className="bg-emerald-50 rounded-lg p-4 space-y-2">
                  {invoiceSummary.is_insurance_invoice && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Insurer Portion:</span>
                        <span className="text-blue-600">${(invoiceSummary.insurer_total || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Patient Portion:</span>
                        <span className="text-emerald-600">${(invoiceSummary.patient_total || 0).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between font-semibold text-lg pt-2 border-t border-emerald-200">
                    <span>Total:</span>
                    <span className="text-emerald-700">${(invoiceSummary.grand_total || 0).toFixed(2)}</span>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="border-t pt-4 space-y-3">
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowInvoiceModal(false)}
                      className="flex-1"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Keep as Draft
                    </Button>
                    <Button
                      onClick={handleFinalizeInvoice}
                      disabled={finalizing || invoiceSummary.line_items?.length === 0}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="finalize-invoice-btn"
                    >
                      {finalizing ? 'Finalizing...' : 'Finalize & Send to Cashier'}
                    </Button>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-700">
                      <strong>Note:</strong> Once finalized, this invoice becomes read-only and will appear in the Cashier portal for payment. 
                      Add all required services before finalizing.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-content, .print-content * { visibility: visible; }
          .print-content { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .card { box-shadow: none !important; border: 1px solid #e2e8f0 !important; break-inside: avoid; margin-bottom: 12px; }
          .print-section { margin-bottom: 12px; }
          .print-text { background: white !important; border: 1px solid #e2e8f0; }
          .print-header { text-align: center; margin-bottom: 16px; }
          .print-signature { margin-top: 30px; }
          .print-notes { max-height: none !important; overflow: visible !important; }
          .note-card { break-inside: avoid; }
        }
        @media screen { .print-only { display: none !important; } }
      `}</style>
    </Layout>
  );
};

export default EncounterDetail;
